import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useAuth } from '@brg/auth'
import {
  BrgButton,
  ButtonVariant,
  FlexBox,
  MagicBoxRow as MagicCheckBox,
  useToasts
} from '@brg/ui'
import { useTranslation } from 'react-i18next'
import { useModal, setModalCloseGuard } from 'core'
import {
  addManagerTestItem,
  buildManagerTestItemAddPayload,
  buildManagerQuestionnaireItemPayload,
  fetchManagerTestItem,
  parseManagerTestItemAddResponse,
  updateManagerTestItem,
  MANAGER_TEST_ITEM_TYPE_QUESTIONNAIRE
} from 'shared/api/managerTestItem'
import {
  pickManagerTestItemStatusCode,
  TEST_MANAGER_ITEM_STATUS_DRAFT,
  TEST_MANAGER_ITEM_STATUS_PUBLISHED,
  unwrapTestItemPayload
} from 'shared/api/testItem'
import {
  fetchManagerQuestionList,
  unwrapManagerQuestionListPayload
} from 'shared/api/managerQuestion'
import {
  mapApiQuestionsToReportDrafts,
  mapExplainTypeToMode,
  mapLocalizedToBilingual,
  timeToCompleteToMinutes
} from './hydrateTestFromApi'
import {
  logTestEditHydration,
  sampleRawFirstQuestion,
  summarizeQuestionDraftsForDisplay
} from './logTestEditHydration'
import { BilingualLangField } from 'shared/components/bilingualLangField'
import RadioButton from 'shared/components/Radio/RadioButton'
import {
  Root,
  Header,
  HeaderSaveSlot,
  MainTitleWrap,
  MainTitle,
  Body,
  Sidebar,
  TabButton,
  MainPanel,
  SectionTitle,
  FieldBlock,
  SettingsExplainFieldBlock,
  SettingsFieldLabel,
  SettingsInput,
  SettingsSubTitle,
  SettingsRadioStack,
  PublishedReadOnlyBanner,
  TabPanelLocked
} from './styles'
import type { QuestionDraft } from './questionTypes'
import { areAllQuestionsValidForDone } from './questionDraftValidation'
import TestCreateQuestionsTab from './TestCreateQuestionsTab'
// import TestCreateReportTab from './TestCreateReportTab'
import ColorPicker, {
  STANDARD_COLOR_PICKER_SWATCHES
} from 'shared/pages/projectEdit/components/ColorPicker/ColorPicker'

const MAX_DESCRIPTION = 300

const DEFAULT_TEST_HEADER_BACKGROUND = '#574AF1'

const TEST_HEADER_SWATCH_PALETTE: string[] = [
  DEFAULT_TEST_HEADER_BACKGROUND,
  ...STANDARD_COLOR_PICKER_SWATCHES.slice(1)
]

function isFilledDurationMinutes(s: string): boolean {
  const t = s.trim()
  if (t === '') {
    return false
  }
  const digits = t.replace(/[^\d]/g, '')
  if (digits === '') {
    return false
  }
  const n = parseInt(digits, 10)
  return Number.isFinite(n) && n >= 1
}

function pickHeaderBackgroundColor(
  data: Record<string, unknown>,
  fallback: string
): string {
  const raw =
    data.bgColor ??
    data.BgColor ??
    data.headerBackgroundColor ??
    data.HeaderBackgroundColor
  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim()
  }
  return fallback
}

/** Вкладка «Отчёт» отключена */
const SHOW_TEST_CREATE_REPORT_TAB = false

type TabId = 'params' | 'settings' | 'questions' | 'report'

type LangCode = 'ru' | 'en'

type Bilingual = Record<LangCode, string>

type ExplainMode = 'never' | 'after_submit' | 'if_passed' | 'after_each'

const emptyBilingual = (): Bilingual => ({ ru: '', en: '' })

const EXPLAIN_GROUP_NAME = 'test-create-explain-mode'

const TestCreateModal: FC<{
  createMode?: 'new' | 'existing'
  productKind?: 'test' | 'questionnaire'
  longreadId?: string
  initialTestItemId?: string
  onFinish?: (payload: { testItemId: string }) => void
}> = ({
  createMode = 'new',
  productKind = 'test',
  longreadId: longreadIdFromArgs,
  initialTestItemId: initialTestItemIdArg,
  onFinish
}) => {
  const { t, i18n } = useTranslation()
  const toasts = useToasts()
  const modal = useModal()
  const { token } = useAuth()
  const [resolvedProductKind, setResolvedProductKind] = useState(productKind)
  const isQuestionnaire = resolvedProductKind === 'questionnaire'
  const longreadId = longreadIdFromArgs

  /** Prefer survey-specific copy when editing/creating a questionnaire. */
  const tm = useCallback(
    (key: string) => {
      if (isQuestionnaire) {
        const surveyKey = `${key}_survey`
        if (i18n.exists(surveyKey)) {
          return t(surveyKey)
        }
      }
      return t(key)
    },
    [isQuestionnaire, i18n, t]
  )

  const [activeTab, setActiveTab] = useState<TabId>('params')
  const [testCreated, setTestCreated] = useState(false)
  const [testItemId, setTestItemId] = useState<string | null>(null)
  const [settingsSaving, setSettingsSaving] = useState(false)
  const [titleLang, setTitleLang] = useState<LangCode>('ru')
  const [descLang, setDescLang] = useState<LangCode>('ru')
  const [title, setTitle] = useState<Bilingual>(emptyBilingual)
  const [description, setDescription] = useState<Bilingual>(emptyBilingual)
  const [headerBackgroundColor, setHeaderBackgroundColor] = useState(
    () => DEFAULT_TEST_HEADER_BACKGROUND
  )

  const [passScore, setPassScore] = useState('')
  const [durationMin, setDurationMin] = useState('')
  const [attemptsLimit, setAttemptsLimit] = useState('1')
  const [access, setAccess] = useState<'open' | 'private'>('open')
  const [shuffleAnswers, setShuffleAnswers] = useState(false)
  const [shuffleQuestions, setShuffleQuestions] = useState(false)
  const [showResultsAfterAnswer, setShowResultsAfterAnswer] = useState(false)
  const [explainMode, setExplainMode] = useState<ExplainMode>('after_submit')
  const [reportQuestions, setReportQuestions] = useState<QuestionDraft[]>([])
  const [questionsHydrationKey, setQuestionsHydrationKey] = useState(0)
  const [isEditMode, setIsEditMode] = useState(false)
  const [managerTestItemStatus, setManagerTestItemStatus] = useState<number | null>(
    null
  )
  /** Edit-open hydrate finished (success or error) — stops «Загрузка вопросов…». */
  const [editHydrateDone, setEditHydrateDone] = useState(false)

  const didFinishWithButtonRef = useRef(false)
  const tmRef = useRef(tm)
  tmRef.current = tm
  const toastsRef = useRef(toasts)
  toastsRef.current = toasts

  const closeGuardStateRef = useRef({
    isNewCreationFlow: false,
    hasDraftProgress: false,
    finishedWithButton: false,
    translate: (_k: string) => ''
  })

  useEffect(() => {
    setModalCloseGuard('testCreate', () => {
      const s = closeGuardStateRef.current
      if (!s.isNewCreationFlow) {
        return true
      }
      if (!s.hasDraftProgress) {
        return true
      }
      if (s.finishedWithButton) {
        return true
      }
      return window.confirm(
        s.translate('modal.test_create.close_confirm_new')
      )
    })
    return () => {
      setModalCloseGuard('testCreate', null)
    }
  }, [])

  const descLength = description[descLang].length

  const hasValidTitle = useMemo(
    () => title.ru.trim().length > 0 || title.en.trim().length > 0,
    [title.ru, title.en]
  )

  const hasValidDurationMin = useMemo(() => {
    if (isQuestionnaire) {
      return true
    }
    return isFilledDurationMinutes(durationMin)
  }, [durationMin, isQuestionnaire])

  const testPublishedLocked = useMemo(
    () => managerTestItemStatus === TEST_MANAGER_ITEM_STATUS_PUBLISHED,
    [managerTestItemStatus]
  )

  closeGuardStateRef.current = {
    isNewCreationFlow: !initialTestItemIdArg?.trim(),
    hasDraftProgress: Boolean(
      testItemId?.trim() ||
        hasValidTitle ||
        hasValidDurationMin ||
        description.ru.trim() ||
        description.en.trim() ||
        passScore.trim() ||
        durationMin.trim() ||
        (() => {
          const n = parseInt(attemptsLimit, 10)
          return Number.isFinite(n) && n !== 1
        })() ||
        shuffleAnswers ||
        shuffleQuestions ||
        showResultsAfterAnswer ||
        reportQuestions.length > 0 ||
        headerBackgroundColor !== DEFAULT_TEST_HEADER_BACKGROUND ||
        explainMode !== 'after_submit'
    ),
    finishedWithButton: didFinishWithButtonRef.current,
    translate: (key: string) => tm(key)
  }

  const setTitleForLang = (lang: LangCode, value: string) => {
    setTitle((prev) => ({ ...prev, [lang]: value }))
  }

  const setDescForLang = (lang: LangCode, value: string) => {
    if (value.length > MAX_DESCRIPTION) return
    setDescription((prev) => ({ ...prev, [lang]: value }))
  }

  const modalTitle = useMemo(() => {
    if (isQuestionnaire) {
      if (isEditMode) {
        return tm('modal.test_create.title_edit')
      }
      if (createMode === 'existing') {
        return tm('modal.test_create.title_from_existing')
      }
      return tm('modal.test_create.title_new')
    }
    if (isEditMode) {
      return t('modal.test_create.title_edit')
    }
    if (createMode === 'existing') {
      return t('modal.test_create.title_from_existing')
    }
    return t('modal.test_create.title_new')
  }, [createMode, t, tm, isEditMode, isQuestionnaire])

  const tabs = useMemo((): ReadonlyArray<{ id: TabId; label: string }> => {
    const base: { id: TabId; label: string }[] = [
      {
        id: 'params',
        label: tm('modal.test_create.tab_params')
      }
    ]
    if (!isQuestionnaire) {
      base.push({ id: 'settings', label: t('modal.test_create.tab_settings') })
    }
    base.push({ id: 'questions', label: t('modal.test_create.tab_questions') })
    return base
  }, [t, tm, isQuestionnaire])

  useEffect(() => {
    setResolvedProductKind(productKind)
  }, [productKind])

  useEffect(() => {
    setTestCreated(false)
    setEditHydrateDone(false)
    const editId = initialTestItemIdArg?.trim() ?? null
    setTestItemId(editId)
    setIsEditMode(Boolean(editId))
    setActiveTab('params')
    setReportQuestions([])
    setQuestionsHydrationKey(0)
    setHeaderBackgroundColor(DEFAULT_TEST_HEADER_BACKGROUND)
    setAttemptsLimit('1')
    setManagerTestItemStatus(null)
    setResolvedProductKind(productKind)
  }, [longreadIdFromArgs, createMode, initialTestItemIdArg, productKind])

  useEffect(() => {
    const id = initialTestItemIdArg?.trim()
    if (!id || !token?.trim()) {
      setEditHydrateDone(true)
      return
    }
    let cancelled = false
    setEditHydrateDone(false)
    setSettingsSaving(true)
    fetchManagerTestItem(id, { accessToken: token })
      .then(async (raw) => {
        if (cancelled) {
          return
        }
        const data = unwrapTestItemPayload(raw)
        if (!data) {
          toastsRef.current.error(tmRef.current('modal.test_create.settings.load_error'))
          return
        }
        const itemType = data.type ?? data.Type
        const typeNum =
          typeof itemType === 'number'
            ? itemType
            : typeof itemType === 'string' && itemType.trim()
              ? parseInt(itemType.trim(), 10)
              : NaN
        const fromApiQuestionnaire =
          typeNum === MANAGER_TEST_ITEM_TYPE_QUESTIONNAIRE
        if (fromApiQuestionnaire) {
          setResolvedProductKind('questionnaire')
        }
        const treatAsQuestionnaire =
          fromApiQuestionnaire || productKind === 'questionnaire'
        setTitle(mapLocalizedToBilingual(data.title))
        setDescription(mapLocalizedToBilingual(data.description))
        {
          const rawScore = data.passingScore
          let scoreDisplay = ''
          if (rawScore != null && rawScore !== '') {
            const n =
              typeof rawScore === 'number'
                ? rawScore
                : parseInt(String(rawScore), 10)
            if (Number.isFinite(n)) {
              scoreDisplay = String(Math.min(100, Math.max(0, Math.floor(n))))
            }
          }
          setPassScore(scoreDisplay)
        }
        {
          const rawAttempts = data.numberOfAttempts
          const n =
            typeof rawAttempts === 'number'
              ? rawAttempts
              : parseInt(String(rawAttempts ?? ''), 10)
          setAttemptsLimit(
            Number.isFinite(n) && n >= 1 ? String(Math.floor(n)) : '1'
          )
        }
        setDurationMin(timeToCompleteToMinutes(data.timeToComplete))
        setAccess(data.accessType === 0 ? 'open' : 'private')
        setShuffleQuestions(
          treatAsQuestionnaire ? false : Boolean(data.shuffleQuestions)
        )
        setShuffleAnswers(Boolean(data.shuffleAnswers))
        setShowResultsAfterAnswer(Boolean(data.showCorrectAnswers))
        setExplainMode(mapExplainTypeToMode(data.showExplanationsType))
        setHeaderBackgroundColor(
          pickHeaderBackgroundColor(data, DEFAULT_TEST_HEADER_BACKGROUND)
        )
        const statusCode =
          pickManagerTestItemStatusCode(data) ?? TEST_MANAGER_ITEM_STATUS_DRAFT
        setManagerTestItemStatus(statusCode)

        let questionsPayload: unknown = data.questions
        let questionsSource:
          | 'ManagerTestItem/Get'
          | 'ManagerQuestion/GetList' = 'ManagerTestItem/Get'
        try {
          const listRaw = await fetchManagerQuestionList(id, {
            accessToken: token
          })
          const fromList = unwrapManagerQuestionListPayload(listRaw)
          if (fromList.length > 0) {
            questionsPayload = fromList
            questionsSource = 'ManagerQuestion/GetList'
          }
        } catch {
        }
        if (cancelled) {
          return
        }
        let hydratedDrafts = mapApiQuestionsToReportDrafts(questionsPayload)
        logTestEditHydration(id, 'редактирование: что пойдёт в превью картинок', () => ({
          questionsSource,
          questionCount: hydratedDrafts.length,
          apiFirstQuestionSample: sampleRawFirstQuestion(questionsPayload),
          draftsDisplay: summarizeQuestionDraftsForDisplay(hydratedDrafts)
        }))
        setReportQuestions(hydratedDrafts)
        setQuestionsHydrationKey((k) => k + 1)
        setTestItemId(id)
        setTestCreated(true)
        setIsEditMode(true)
      })
      .catch((e) => {
        if (!cancelled) {
          const msg =
            e instanceof Error
              ? e.message
              : tmRef.current('modal.test_create.settings.load_error')
          toastsRef.current.error(msg)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSettingsSaving(false)
          setEditHydrateDone(true)
        }
      })

    return () => {
      cancelled = true
    }
  }, [initialTestItemIdArg, token, productKind])

  const handleQuestionsChange = useCallback((questions: QuestionDraft[]) => {
    setReportQuestions(questions)
  }, [])

  const showQuestionsTabContent = Boolean(
    testItemId && (testCreated || !isEditMode || editHydrateDone)
  )

  const showQuestionsLoading = Boolean(
    testItemId &&
      activeTab === 'questions' &&
      isEditMode &&
      !editHydrateDone
  )

  const allQuestionsValidForDone = useMemo(
    () => areAllQuestionsValidForDone(reportQuestions, resolvedProductKind),
    [reportQuestions, resolvedProductKind]
  )

  const canFinishTest = Boolean(
    testCreated && testItemId && allQuestionsValidForDone
  )

  const handleFinish = useCallback(async () => {
    const id = testItemId?.trim()
    if (!id || !canFinishTest) {
      return
    }
    if (testPublishedLocked) {
      toasts.error(tm('modal.test_create.settings.published_no_edit'))
      return
    }
    if (!longreadId) {
      toasts.error(t('modal.test_create.settings.save_no_longread'))
      return
    }
    if (!token?.trim()) {
      toasts.error(t('modal.test_create.settings.save_not_authorized'))
      return
    }
    if (!hasValidTitle) {
      toasts.error(tm('modal.test_create.title_required'))
      return
    }
    if (!hasValidDurationMin) {
      toasts.error(t('modal.test_create.settings.duration_required'))
      return
    }
    setSettingsSaving(true)
    try {
      const payload = isQuestionnaire
        ? buildManagerQuestionnaireItemPayload({
            entityId: longreadId,
            title,
            description,
            headerBackgroundColor
          })
        : buildManagerTestItemAddPayload({
            entityId: longreadId,
            title,
            description,
            headerBackgroundColor,
            passScore,
            durationMin,
            attemptsLimit,
            access,
            shuffleQuestions,
            shuffleAnswers,
            showResultsAfterAnswer,
            explainMode
          })
      await updateManagerTestItem(
        { id, ...payload },
        { accessToken: token }
      )
      didFinishWithButtonRef.current = true
      onFinish?.({ testItemId: id })
      modal.hide()
      toasts.info(tm('modal.test_create.finish_success'))
    } catch (e) {
      const message =
        e instanceof Error ? e.message : t('modal.test_create.settings.save_error')
      toasts.error(message)
    } finally {
      setSettingsSaving(false)
    }
  }, [
    testItemId,
    canFinishTest,
    longreadId,
    token,
    hasValidTitle,
    hasValidDurationMin,
    title,
    description,
    headerBackgroundColor,
    passScore,
    durationMin,
    attemptsLimit,
    access,
    shuffleQuestions,
    shuffleAnswers,
    showResultsAfterAnswer,
    explainMode,
    onFinish,
    modal,
    toasts,
    t,
    tm,
    testPublishedLocked,
    isQuestionnaire
  ])

  const isTabLocked = useCallback(
    (tabId: TabId) => {
      if ((tabId === 'questions' || tabId === 'report') && !testItemId) {
        return true
      }
      return false
    },
    [testItemId]
  )

  useEffect(() => {
    if (!testItemId && (activeTab === 'questions' || activeTab === 'report')) {
      setActiveTab('params')
    }
  }, [testItemId, activeTab])

  useEffect(() => {
    if (isQuestionnaire && activeTab === 'settings') {
      setActiveTab('params')
    }
  }, [isQuestionnaire, activeTab])

  useEffect(() => {
    if (!SHOW_TEST_CREATE_REPORT_TAB && activeTab === 'report') {
      setActiveTab('questions')
    }
  }, [activeTab])

  const handleSaveSettings = useCallback(async () => {
    if (testPublishedLocked) {
      toasts.error(tm('modal.test_create.settings.published_no_edit'))
      return
    }
    if (!longreadId) {
      toasts.error(t('modal.test_create.settings.save_no_longread'))
      return
    }
    if (!token?.trim()) {
      toasts.error(t('modal.test_create.settings.save_not_authorized'))
      return
    }
    if (!hasValidTitle) {
      toasts.error(tm('modal.test_create.title_required'))
      return
    }
    if (!hasValidDurationMin) {
      toasts.error(t('modal.test_create.settings.duration_required'))
      return
    }
    setSettingsSaving(true)
    try {
      const payload = isQuestionnaire
        ? buildManagerQuestionnaireItemPayload({
            entityId: longreadId,
            title,
            description,
            headerBackgroundColor
          })
        : buildManagerTestItemAddPayload({
            entityId: longreadId,
            title,
            description,
            headerBackgroundColor,
            passScore,
            durationMin,
            attemptsLimit,
            access,
            shuffleQuestions,
            shuffleAnswers,
            showResultsAfterAnswer,
            explainMode
          })
      if (testItemId?.trim()) {
        await updateManagerTestItem(
          { id: testItemId.trim(), ...payload },
          { accessToken: token }
        )
        toasts.info(tm('modal.test_create.settings.save_success'))
        return
      }
      const raw = await addManagerTestItem(payload, { accessToken: token })
      const createdId = parseManagerTestItemAddResponse(raw)
      if (!createdId) {
        toasts.error(tm('modal.test_create.settings.save_no_test_item_id'))
        return
      }
      setTestItemId(createdId)
      setTestCreated(true)
      setManagerTestItemStatus(TEST_MANAGER_ITEM_STATUS_DRAFT)
      toasts.info(tm('modal.test_create.settings.save_success'))
    } catch (e) {
      const message =
        e instanceof Error ? e.message : t('modal.test_create.settings.save_error')
      toasts.error(message)
    } finally {
      setSettingsSaving(false)
    }
  }, [
    longreadId,
    token,
    toasts,
    t,
    title,
    description,
    headerBackgroundColor,
    passScore,
    durationMin,
    attemptsLimit,
    access,
    shuffleQuestions,
    shuffleAnswers,
    showResultsAfterAnswer,
    explainMode,
    testItemId,
    hasValidTitle,
    hasValidDurationMin,
    testPublishedLocked,
    isQuestionnaire,
    tm
  ])

  return (
    <Root>
      <Header direction='row' align='center'>
        <MainTitleWrap>
          <MainTitle>{modalTitle}</MainTitle>
        </MainTitleWrap>
        {(activeTab === 'settings' ||
          (isQuestionnaire && activeTab === 'params')) && (
          <HeaderSaveSlot>
            <BrgButton
              variant={ButtonVariant.Filled}
              fullWidth
              disabled={
                settingsSaving ||
                testPublishedLocked ||
                !longreadId ||
                !token?.trim() ||
                !hasValidTitle ||
                !hasValidDurationMin
              }
              onClick={handleSaveSettings}
            >
              {isEditMode
                ? t('modal.test_create.finish_button')
                : t('modal.test_create.settings.save_button')}
            </BrgButton>
          </HeaderSaveSlot>
        )}
        {activeTab !== 'settings' &&
          !(isQuestionnaire && activeTab === 'params') &&
          canFinishTest && (
          <HeaderSaveSlot>
            <BrgButton
              variant={ButtonVariant.Filled}
              fullWidth
              disabled={
                settingsSaving ||
                testPublishedLocked ||
                !longreadId ||
                !token?.trim() ||
                !hasValidTitle ||
                !hasValidDurationMin
              }
              onClick={() => void handleFinish()}
            >
              {t('modal.test_create.finish_button')}
            </BrgButton>
          </HeaderSaveSlot>
        )}
      </Header>

      <Body direction='row'>
        <Sidebar>
          {tabs.map((tab) => {
            const locked = isTabLocked(tab.id)
            const isActive = activeTab === tab.id && !locked
            return (
              <TabButton
                key={tab.id}
                type='button'
                $active={isActive}
                $disabled={locked}
                disabled={locked}
                onClick={() => {
                  if (locked) return
                  setActiveTab(tab.id)
                }}
              >
                {tab.label}
              </TabButton>
            )
          })}
        </Sidebar>

        <MainPanel>
          {testPublishedLocked && (
            <PublishedReadOnlyBanner role='status'>
              {tm('modal.test_create.published_readonly_banner')}
            </PublishedReadOnlyBanner>
          )}

          {activeTab === 'params' && (
            <TabPanelLocked $locked={testPublishedLocked}>
              <SectionTitle>{tm('modal.test_create.section_params')}</SectionTitle>

              <FieldBlock>
                <BilingualLangField
                  label={tm('modal.test_create.field_title')}
                  required
                  activeLang={titleLang}
                  onLangChange={setTitleLang}
                  value={title[titleLang]}
                  onChange={(v) => setTitleForLang(titleLang, v)}
                  placeholder={tm('modal.test_create.placeholder_title')}
                  maxLength={500}
                  inputMode='text'
                />
              </FieldBlock>

              <FieldBlock>
                <BilingualLangField
                  label={tm('modal.test_create.field_description')}
                  activeLang={descLang}
                  onLangChange={setDescLang}
                  value={description[descLang]}
                  onChange={(v) => setDescForLang(descLang, v)}
                  placeholder={tm('modal.test_create.placeholder_description')}
                  maxLength={MAX_DESCRIPTION}
                  inputMode='textarea'
                  charCount={{ current: descLength, max: MAX_DESCRIPTION }}
                />
              </FieldBlock>

              <FieldBlock>
                <ColorPicker
                  value={headerBackgroundColor}
                  onChange={setHeaderBackgroundColor}
                  formLabel={t('modal.test_create.header_background')}
                  showOnMobile
                  customPickerFirst
                  swatchColors={TEST_HEADER_SWATCH_PALETTE}
                />
              </FieldBlock>
            </TabPanelLocked>
          )}

          {activeTab === 'settings' && !isQuestionnaire && (
            <TabPanelLocked $locked={testPublishedLocked}>
              <SectionTitle>
                {t('modal.test_create.settings.section_title')}
              </SectionTitle>

              <>
              <FieldBlock>
                <SettingsFieldLabel>
                  {t('modal.test_create.settings.pass_score')}
                </SettingsFieldLabel>
                <SettingsInput
                  type='text'
                  inputMode='numeric'
                  autoComplete='off'
                  value={passScore}
                  onChange={(e) => {
                    const v = e.target.value.trim()
                    if (v === '') {
                      setPassScore('')
                      return
                    }
                    const digits = v.replace(/[^\d]/g, '')
                    if (digits === '') {
                      setPassScore('')
                      return
                    }
                    const n = parseInt(digits, 10)
                    if (!Number.isFinite(n)) {
                      setPassScore('')
                      return
                    }
                    setPassScore(String(Math.min(100, Math.max(0, n))))
                  }}
                />
              </FieldBlock>

              <FieldBlock>
                <SettingsFieldLabel $required>
                  {t('modal.test_create.settings.duration_min')}
                </SettingsFieldLabel>
                <SettingsInput
                  type='text'
                  inputMode='numeric'
                  autoComplete='off'
                  value={durationMin}
                  onChange={(e) => {
                    const v = e.target.value.trim()
                    if (v === '') {
                      setDurationMin('')
                      return
                    }
                    const digits = v.replace(/[^\d]/g, '')
                    if (digits === '') {
                      setDurationMin('')
                      return
                    }
                    const n = parseInt(digits, 10)
                    if (!Number.isFinite(n)) {
                      setDurationMin('')
                      return
                    }
                    if (n < 1) {
                      setDurationMin('')
                      return
                    }
                    setDurationMin(String(n))
                  }}
                />
              </FieldBlock>

              <FieldBlock>
                <SettingsFieldLabel>
                  {t('modal.test_create.settings.attempts_limit')}
                </SettingsFieldLabel>
                <SettingsInput
                  type='text'
                  inputMode='numeric'
                  autoComplete='off'
                  value={attemptsLimit}
                  onChange={(e) => {
                    const v = e.target.value.trim()
                    if (v === '') {
                      setAttemptsLimit('1')
                      return
                    }
                    const digits = v.replace(/[^\d]/g, '')
                    if (digits === '') {
                      setAttemptsLimit('1')
                      return
                    }
                    const n = parseInt(digits, 10)
                    if (!Number.isFinite(n)) {
                      setAttemptsLimit('1')
                      return
                    }
                    setAttemptsLimit(String(Math.max(1, n)))
                  }}
                />
              </FieldBlock>

              {/* Поле «Доступ» временно скрыто
              <FieldBlock>
                <CommonInput
                  label={t('modal.test_create.settings.access_label')}
                  orientation="column"
                  type={INPUT_TYPES.dropdown}
                  options={accessOptions as any}
                  value={accessValue as any}
                  onWhiteBG
                  onChange={(item: any) => {
                    const v = item?.value
                    if (v === 'open' || v === 'private') {
                      setAccess(v)
                    }
                  }}
                />
              </FieldBlock>
              */}

              <FieldBlock>
                <FlexBox direction='column' gy={14} style={{ width: '100%' }}>
                  <MagicCheckBox
                    checked={shuffleAnswers}
                    label={t('modal.test_create.settings.shuffle_answers')}
                    onWhiteBG
                    onClick={() => setShuffleAnswers((prev) => !prev)}
                  />
                  <MagicCheckBox
                    checked={shuffleQuestions}
                    label={t('modal.test_create.settings.shuffle_questions')}
                    onWhiteBG
                    onClick={() => setShuffleQuestions((prev) => !prev)}
                  />
                  <MagicCheckBox
                    checked={showResultsAfterAnswer}
                    label={t(
                      'modal.test_create.settings.show_results_after_answer'
                    )}
                    onWhiteBG
                    onClick={() =>
                      setShowResultsAfterAnswer((prev) => !prev)
                    }
                  />
                </FlexBox>
              </FieldBlock>

              <SettingsExplainFieldBlock $muted={!showResultsAfterAnswer}>
                <SettingsSubTitle>
                  {t('modal.test_create.settings.explain_title')}
                </SettingsSubTitle>
                <SettingsRadioStack>
                  <RadioButton
                    name={EXPLAIN_GROUP_NAME}
                    checked={explainMode === 'never'}
                    disabled={!showResultsAfterAnswer}
                    onChange={() => setExplainMode('never')}
                  >
                    <span className='radio-text'>
                      {t('modal.test_create.settings.explain_never')}
                    </span>
                  </RadioButton>
                  <RadioButton
                    name={EXPLAIN_GROUP_NAME}
                    checked={explainMode === 'after_submit'}
                    disabled={!showResultsAfterAnswer}
                    onChange={() => setExplainMode('after_submit')}
                  >
                    <span className='radio-text'>
                      {t('modal.test_create.settings.explain_after_submit')}
                    </span>
                  </RadioButton>
                  <RadioButton
                    name={EXPLAIN_GROUP_NAME}
                    checked={explainMode === 'if_passed'}
                    disabled={!showResultsAfterAnswer}
                    onChange={() => setExplainMode('if_passed')}
                  >
                    <span className='radio-text'>
                      {t('modal.test_create.settings.explain_if_passed')}
                    </span>
                  </RadioButton>
                  <RadioButton
                    name={EXPLAIN_GROUP_NAME}
                    checked={explainMode === 'after_each'}
                    disabled={!showResultsAfterAnswer}
                    onChange={() => setExplainMode('after_each')}
                  >
                    <span className='radio-text'>
                      {t('modal.test_create.settings.explain_after_each')}
                    </span>
                  </RadioButton>
                </SettingsRadioStack>
              </SettingsExplainFieldBlock>
              </>
            </TabPanelLocked>
          )}

          {showQuestionsLoading && (
            <SectionTitle>{t('modal.test_create.questions.loading')}</SectionTitle>
          )}

          {showQuestionsTabContent && (
            <TabPanelLocked
              $locked={testPublishedLocked}
              style={{
                display: activeTab === 'questions' ? 'block' : 'none'
              }}
            >
              <TestCreateQuestionsTab
                key={`${testItemId ?? 'new'}-${questionsHydrationKey}`}
                productKind={resolvedProductKind}
                testItemId={testItemId}
                accessToken={token}
                longreadId={longreadId}
                initialQuestions={reportQuestions}
                onQuestionsChange={handleQuestionsChange}
              />
            </TabPanelLocked>
          )}

          {/* {SHOW_TEST_CREATE_REPORT_TAB && activeTab === 'report' && (
            <TestCreateReportTab
              testTitle={title[titleLang]}
              testDescription={description[titleLang]}
              questions={reportQuestions}
              titleLang={titleLang}
              passScore={passScore}
              durationMin={durationMin}
              attemptsLimit={attemptsLimit}
              authorName={reportTabAuthorName}
              createdAtIso={testReportMeta.createdAtIso}
              testStatus={testReportMeta.status}
            />
          )} */}
        </MainPanel>
      </Body>
    </Root>
  )
}

export default TestCreateModal
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useAuth } from '@brg/auth'
import {
  BrgButton,
  ButtonVariant,
  FlexBox,
  MagicBoxRow as MagicCheckBox,
  useToasts
} from '@brg/ui'
import { useTranslation } from 'react-i18next'
import { useModal, setModalCloseGuard } from 'core'
import {
  addManagerTestItem,
  buildManagerTestItemAddPayload,
  buildManagerQuestionnaireItemPayload,
  fetchManagerTestItem,
  parseManagerTestItemAddResponse,
  updateManagerTestItem,
  MANAGER_TEST_ITEM_TYPE_QUESTIONNAIRE
} from 'shared/api/managerTestItem'
import {
  pickManagerTestItemStatusCode,
  TEST_MANAGER_ITEM_STATUS_DRAFT,
  TEST_MANAGER_ITEM_STATUS_PUBLISHED,
  unwrapTestItemPayload
} from 'shared/api/testItem'
import {
  fetchManagerQuestionList,
  unwrapManagerQuestionListPayload
} from 'shared/api/managerQuestion'
import {
  mapApiQuestionsToReportDrafts,
  mapExplainTypeToMode,
  mapLocalizedToBilingual,
  timeToCompleteToMinutes
} from './hydrateTestFromApi'
import {
  logTestEditHydration,
  sampleRawFirstQuestion,
  summarizeQuestionDraftsForDisplay
} from './logTestEditHydration'
import { BilingualLangField } from 'shared/components/bilingualLangField'
import RadioButton from 'shared/components/Radio/RadioButton'
import {
  Root,
  Header,
  HeaderSaveSlot,
  MainTitleWrap,
  MainTitle,
  Body,
  Sidebar,
  TabButton,
  MainPanel,
  SectionTitle,
  FieldBlock,
  SettingsExplainFieldBlock,
  SettingsFieldLabel,
  SettingsInput,
  SettingsSubTitle,
  SettingsRadioStack,
  PublishedReadOnlyBanner,
  TabPanelLocked
} from './styles'
import type { QuestionDraft } from './questionTypes'
import { areAllQuestionsValidForDone } from './questionDraftValidation'
import TestCreateQuestionsTab from './TestCreateQuestionsTab'
// import TestCreateReportTab from './TestCreateReportTab'
import ColorPicker, {
  STANDARD_COLOR_PICKER_SWATCHES
} from 'shared/pages/projectEdit/components/ColorPicker/ColorPicker'

const MAX_DESCRIPTION = 300

const DEFAULT_TEST_HEADER_BACKGROUND = '#574AF1'

const TEST_HEADER_SWATCH_PALETTE: string[] = [
  DEFAULT_TEST_HEADER_BACKGROUND,
  ...STANDARD_COLOR_PICKER_SWATCHES.slice(1)
]

function isFilledDurationMinutes(s: string): boolean {
  const t = s.trim()
  if (t === '') {
    return false
  }
  const digits = t.replace(/[^\d]/g, '')
  if (digits === '') {
    return false
  }
  const n = parseInt(digits, 10)
  return Number.isFinite(n) && n >= 1
}

function pickHeaderBackgroundColor(
  data: Record<string, unknown>,
  fallback: string
): string {
  const raw =
    data.bgColor ??
    data.BgColor ??
    data.headerBackgroundColor ??
    data.HeaderBackgroundColor
  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim()
  }
  return fallback
}

/** Вкладка «Отчёт» отключена */
const SHOW_TEST_CREATE_REPORT_TAB = false

type TabId = 'params' | 'settings' | 'questions' | 'report'

type LangCode = 'ru' | 'en'

type Bilingual = Record<LangCode, string>

type ExplainMode = 'never' | 'after_submit' | 'if_passed' | 'after_each'

const emptyBilingual = (): Bilingual => ({ ru: '', en: '' })

const EXPLAIN_GROUP_NAME = 'test-create-explain-mode'

const TestCreateModal: FC<{
  createMode?: 'new' | 'existing'
  productKind?: 'test' | 'questionnaire'
  longreadId?: string
  initialTestItemId?: string
  onFinish?: (payload: { testItemId: string }) => void
}> = ({
  createMode = 'new',
  productKind = 'test',
  longreadId: longreadIdFromArgs,
  initialTestItemId: initialTestItemIdArg,
  onFinish
}) => {
  const { t, i18n } = useTranslation()
  const toasts = useToasts()
  const modal = useModal()
  const { token } = useAuth()
  const [resolvedProductKind, setResolvedProductKind] = useState(productKind)
  const isQuestionnaire = resolvedProductKind === 'questionnaire'
  const longreadId = longreadIdFromArgs

  /** Prefer survey-specific copy when editing/creating a questionnaire. */
  const tm = useCallback(
    (key: string) => {
      if (isQuestionnaire) {
        const surveyKey = `${key}_survey`
        if (i18n.exists(surveyKey)) {
          return t(surveyKey)
        }
      }
      return t(key)
    },
    [isQuestionnaire, i18n, t]
  )

  const [activeTab, setActiveTab] = useState<TabId>('params')
  const [testCreated, setTestCreated] = useState(false)
  const [testItemId, setTestItemId] = useState<string | null>(null)
  const [settingsSaving, setSettingsSaving] = useState(false)
  const [titleLang, setTitleLang] = useState<LangCode>('ru')
  const [descLang, setDescLang] = useState<LangCode>('ru')
  const [title, setTitle] = useState<Bilingual>(emptyBilingual)
  const [description, setDescription] = useState<Bilingual>(emptyBilingual)
  const [headerBackgroundColor, setHeaderBackgroundColor] = useState(
    () => DEFAULT_TEST_HEADER_BACKGROUND
  )

  const [passScore, setPassScore] = useState('')
  const [durationMin, setDurationMin] = useState('')
  const [attemptsLimit, setAttemptsLimit] = useState('1')
  const [access, setAccess] = useState<'open' | 'private'>('open')
  const [shuffleAnswers, setShuffleAnswers] = useState(false)
  const [shuffleQuestions, setShuffleQuestions] = useState(false)
  const [showResultsAfterAnswer, setShowResultsAfterAnswer] = useState(false)
  const [explainMode, setExplainMode] = useState<ExplainMode>('after_submit')
  const [reportQuestions, setReportQuestions] = useState<QuestionDraft[]>([])
  const [questionsHydrationKey, setQuestionsHydrationKey] = useState(0)
  const [isEditMode, setIsEditMode] = useState(false)
  const [managerTestItemStatus, setManagerTestItemStatus] = useState<number | null>(
    null
  )
  /** Edit-open hydrate finished (success or error) — stops «Загрузка вопросов…». */
  const [editHydrateDone, setEditHydrateDone] = useState(false)

  const didFinishWithButtonRef = useRef(false)
  const tmRef = useRef(tm)
  tmRef.current = tm
  const toastsRef = useRef(toasts)
  toastsRef.current = toasts

  const closeGuardStateRef = useRef({
    isNewCreationFlow: false,
    hasDraftProgress: false,
    finishedWithButton: false,
    translate: (_k: string) => ''
  })

  useEffect(() => {
    setModalCloseGuard('testCreate', () => {
      const s = closeGuardStateRef.current
      if (!s.isNewCreationFlow) {
        return true
      }
      if (!s.hasDraftProgress) {
        return true
      }
      if (s.finishedWithButton) {
        return true
      }
      return window.confirm(
        s.translate('modal.test_create.close_confirm_new')
      )
    })
    return () => {
      setModalCloseGuard('testCreate', null)
    }
  }, [])

  const descLength = description[descLang].length

  const hasValidTitle = useMemo(
    () => title.ru.trim().length > 0 || title.en.trim().length > 0,
    [title.ru, title.en]
  )

  const hasValidDurationMin = useMemo(() => {
    if (isQuestionnaire) {
      return true
    }
    return isFilledDurationMinutes(durationMin)
  }, [durationMin, isQuestionnaire])

  const testPublishedLocked = useMemo(
    () => managerTestItemStatus === TEST_MANAGER_ITEM_STATUS_PUBLISHED,
    [managerTestItemStatus]
  )

  closeGuardStateRef.current = {
    isNewCreationFlow: !initialTestItemIdArg?.trim(),
    hasDraftProgress: Boolean(
      testItemId?.trim() ||
        hasValidTitle ||
        hasValidDurationMin ||
        description.ru.trim() ||
        description.en.trim() ||
        passScore.trim() ||
        durationMin.trim() ||
        (() => {
          const n = parseInt(attemptsLimit, 10)
          return Number.isFinite(n) && n !== 1
        })() ||
        shuffleAnswers ||
        shuffleQuestions ||
        showResultsAfterAnswer ||
        reportQuestions.length > 0 ||
        headerBackgroundColor !== DEFAULT_TEST_HEADER_BACKGROUND ||
        explainMode !== 'after_submit'
    ),
    finishedWithButton: didFinishWithButtonRef.current,
    translate: (key: string) => tm(key)
  }

  const setTitleForLang = (lang: LangCode, value: string) => {
    setTitle((prev) => ({ ...prev, [lang]: value }))
  }

  const setDescForLang = (lang: LangCode, value: string) => {
    if (value.length > MAX_DESCRIPTION) return
    setDescription((prev) => ({ ...prev, [lang]: value }))
  }

  const modalTitle = useMemo(() => {
    if (isQuestionnaire) {
      if (isEditMode) {
        return tm('modal.test_create.title_edit')
      }
      if (createMode === 'existing') {
        return tm('modal.test_create.title_from_existing')
      }
      return tm('modal.test_create.title_new')
    }
    if (isEditMode) {
      return t('modal.test_create.title_edit')
    }
    if (createMode === 'existing') {
      return t('modal.test_create.title_from_existing')
    }
    return t('modal.test_create.title_new')
  }, [createMode, t, tm, isEditMode, isQuestionnaire])

  const tabs = useMemo((): ReadonlyArray<{ id: TabId; label: string }> => {
    const base: { id: TabId; label: string }[] = [
      {
        id: 'params',
        label: tm('modal.test_create.tab_params')
      }
    ]
    if (!isQuestionnaire) {
      base.push({ id: 'settings', label: t('modal.test_create.tab_settings') })
    }
    base.push({ id: 'questions', label: t('modal.test_create.tab_questions') })
    return base
  }, [t, tm, isQuestionnaire])

  useEffect(() => {
    setResolvedProductKind(productKind)
  }, [productKind])

  useEffect(() => {
    setTestCreated(false)
    setEditHydrateDone(false)
    const editId = initialTestItemIdArg?.trim() ?? null
    setTestItemId(editId)
    setIsEditMode(Boolean(editId))
    setActiveTab('params')
    setReportQuestions([])
    setQuestionsHydrationKey(0)
    setHeaderBackgroundColor(DEFAULT_TEST_HEADER_BACKGROUND)
    setAttemptsLimit('1')
    setManagerTestItemStatus(null)
    setResolvedProductKind(productKind)
  }, [longreadIdFromArgs, createMode, initialTestItemIdArg, productKind])

  useEffect(() => {
    const id = initialTestItemIdArg?.trim()
    if (!id || !token?.trim()) {
      setEditHydrateDone(true)
      return
    }
    let cancelled = false
    setEditHydrateDone(false)
    setSettingsSaving(true)
    fetchManagerTestItem(id, { accessToken: token })
      .then(async (raw) => {
        if (cancelled) {
          return
        }
        const data = unwrapTestItemPayload(raw)
        if (!data) {
          toastsRef.current.error(tmRef.current('modal.test_create.settings.load_error'))
          return
        }
        const itemType = data.type ?? data.Type
        const typeNum =
          typeof itemType === 'number'
            ? itemType
            : typeof itemType === 'string' && itemType.trim()
              ? parseInt(itemType.trim(), 10)
              : NaN
        const fromApiQuestionnaire =
          typeNum === MANAGER_TEST_ITEM_TYPE_QUESTIONNAIRE
        if (fromApiQuestionnaire) {
          setResolvedProductKind('questionnaire')
        }
        const treatAsQuestionnaire =
          fromApiQuestionnaire || productKind === 'questionnaire'
        setTitle(mapLocalizedToBilingual(data.title))
        setDescription(mapLocalizedToBilingual(data.description))
        {
          const rawScore = data.passingScore
          let scoreDisplay = ''
          if (rawScore != null && rawScore !== '') {
            const n =
              typeof rawScore === 'number'
                ? rawScore
                : parseInt(String(rawScore), 10)
            if (Number.isFinite(n)) {
              scoreDisplay = String(Math.min(100, Math.max(0, Math.floor(n))))
            }
          }
          setPassScore(scoreDisplay)
        }
        {
          const rawAttempts = data.numberOfAttempts
          const n =
            typeof rawAttempts === 'number'
              ? rawAttempts
              : parseInt(String(rawAttempts ?? ''), 10)
          setAttemptsLimit(
            Number.isFinite(n) && n >= 1 ? String(Math.floor(n)) : '1'
          )
        }
        setDurationMin(timeToCompleteToMinutes(data.timeToComplete))
        setAccess(data.accessType === 0 ? 'open' : 'private')
        setShuffleQuestions(
          treatAsQuestionnaire ? false : Boolean(data.shuffleQuestions)
        )
        setShuffleAnswers(Boolean(data.shuffleAnswers))
        setShowResultsAfterAnswer(Boolean(data.showCorrectAnswers))
        setExplainMode(mapExplainTypeToMode(data.showExplanationsType))
        setHeaderBackgroundColor(
          pickHeaderBackgroundColor(data, DEFAULT_TEST_HEADER_BACKGROUND)
        )
        const statusCode =
          pickManagerTestItemStatusCode(data) ?? TEST_MANAGER_ITEM_STATUS_DRAFT
        setManagerTestItemStatus(statusCode)

        let questionsPayload: unknown = data.questions
        let questionsSource:
          | 'ManagerTestItem/Get'
          | 'ManagerQuestion/GetList' = 'ManagerTestItem/Get'
        try {
          const listRaw = await fetchManagerQuestionList(id, {
            accessToken: token
          })
          const fromList = unwrapManagerQuestionListPayload(listRaw)
          if (fromList.length > 0) {
            questionsPayload = fromList
            questionsSource = 'ManagerQuestion/GetList'
          }
        } catch {
        }
        if (cancelled) {
          return
        }
        let hydratedDrafts = mapApiQuestionsToReportDrafts(questionsPayload)
        logTestEditHydration(id, 'редактирование: что пойдёт в превью картинок', () => ({
          questionsSource,
          questionCount: hydratedDrafts.length,
          apiFirstQuestionSample: sampleRawFirstQuestion(questionsPayload),
          draftsDisplay: summarizeQuestionDraftsForDisplay(hydratedDrafts)
        }))
        setReportQuestions(hydratedDrafts)
        setQuestionsHydrationKey((k) => k + 1)
        setTestItemId(id)
        setTestCreated(true)
        setIsEditMode(true)
      })
      .catch((e) => {
        if (!cancelled) {
          const msg =
            e instanceof Error
              ? e.message
              : tmRef.current('modal.test_create.settings.load_error')
          toastsRef.current.error(msg)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSettingsSaving(false)
          setEditHydrateDone(true)
        }
      })

    return () => {
      cancelled = true
    }
  }, [initialTestItemIdArg, token, productKind])

  const handleQuestionsChange = useCallback((questions: QuestionDraft[]) => {
    setReportQuestions(questions)
  }, [])

  const showQuestionsTabContent = Boolean(
    testItemId && (testCreated || !isEditMode || editHydrateDone)
  )

  const showQuestionsLoading = Boolean(
    testItemId &&
      activeTab === 'questions' &&
      isEditMode &&
      !editHydrateDone
  )

  const allQuestionsValidForDone = useMemo(
    () => areAllQuestionsValidForDone(reportQuestions, resolvedProductKind),
    [reportQuestions, resolvedProductKind]
  )

  const canFinishTest = Boolean(
    testCreated && testItemId && allQuestionsValidForDone
  )

  const handleFinish = useCallback(async () => {
    const id = testItemId?.trim()
    if (!id || !canFinishTest) {
      return
    }
    if (testPublishedLocked) {
      toasts.error(tm('modal.test_create.settings.published_no_edit'))
      return
    }
    if (!longreadId) {
      toasts.error(t('modal.test_create.settings.save_no_longread'))
      return
    }
    if (!token?.trim()) {
      toasts.error(t('modal.test_create.settings.save_not_authorized'))
      return
    }
    if (!hasValidTitle) {
      toasts.error(tm('modal.test_create.title_required'))
      return
    }
    if (!hasValidDurationMin) {
      toasts.error(t('modal.test_create.settings.duration_required'))
      return
    }
    setSettingsSaving(true)
    try {
      const payload = isQuestionnaire
        ? buildManagerQuestionnaireItemPayload({
            entityId: longreadId,
            title,
            description,
            headerBackgroundColor
          })
        : buildManagerTestItemAddPayload({
            entityId: longreadId,
            title,
            description,
            headerBackgroundColor,
            passScore,
            durationMin,
            attemptsLimit,
            access,
            shuffleQuestions,
            shuffleAnswers,
            showResultsAfterAnswer,
            explainMode
          })
      await updateManagerTestItem(
        { id, ...payload },
        { accessToken: token }
      )
      didFinishWithButtonRef.current = true
      onFinish?.({ testItemId: id })
      modal.hide()
      toasts.info(tm('modal.test_create.finish_success'))
    } catch (e) {
      const message =
        e instanceof Error ? e.message : t('modal.test_create.settings.save_error')
      toasts.error(message)
    } finally {
      setSettingsSaving(false)
    }
  }, [
    testItemId,
    canFinishTest,
    longreadId,
    token,
    hasValidTitle,
    hasValidDurationMin,
    title,
    description,
    headerBackgroundColor,
    passScore,
    durationMin,
    attemptsLimit,
    access,
    shuffleQuestions,
    shuffleAnswers,
    showResultsAfterAnswer,
    explainMode,
    onFinish,
    modal,
    toasts,
    t,
    tm,
    testPublishedLocked,
    isQuestionnaire
  ])

  const isTabLocked = useCallback(
    (tabId: TabId) => {
      if ((tabId === 'questions' || tabId === 'report') && !testItemId) {
        return true
      }
      return false
    },
    [testItemId]
  )

  useEffect(() => {
    if (!testItemId && (activeTab === 'questions' || activeTab === 'report')) {
      setActiveTab('params')
    }
  }, [testItemId, activeTab])

  useEffect(() => {
    if (isQuestionnaire && activeTab === 'settings') {
      setActiveTab('params')
    }
  }, [isQuestionnaire, activeTab])

  useEffect(() => {
    if (!SHOW_TEST_CREATE_REPORT_TAB && activeTab === 'report') {
      setActiveTab('questions')
    }
  }, [activeTab])

  const handleSaveSettings = useCallback(async () => {
    if (testPublishedLocked) {
      toasts.error(tm('modal.test_create.settings.published_no_edit'))
      return
    }
    if (!longreadId) {
      toasts.error(t('modal.test_create.settings.save_no_longread'))
      return
    }
    if (!token?.trim()) {
      toasts.error(t('modal.test_create.settings.save_not_authorized'))
      return
    }
    if (!hasValidTitle) {
      toasts.error(tm('modal.test_create.title_required'))
      return
    }
    if (!hasValidDurationMin) {
      toasts.error(t('modal.test_create.settings.duration_required'))
      return
    }
    setSettingsSaving(true)
    try {
      const payload = isQuestionnaire
        ? buildManagerQuestionnaireItemPayload({
            entityId: longreadId,
            title,
            description,
            headerBackgroundColor
          })
        : buildManagerTestItemAddPayload({
            entityId: longreadId,
            title,
            description,
            headerBackgroundColor,
            passScore,
            durationMin,
            attemptsLimit,
            access,
            shuffleQuestions,
            shuffleAnswers,
            showResultsAfterAnswer,
            explainMode
          })
      if (testItemId?.trim()) {
        await updateManagerTestItem(
          { id: testItemId.trim(), ...payload },
          { accessToken: token }
        )
        toasts.info(tm('modal.test_create.settings.save_success'))
        return
      }
      const raw = await addManagerTestItem(payload, { accessToken: token })
      const createdId = parseManagerTestItemAddResponse(raw)
      if (!createdId) {
        toasts.error(tm('modal.test_create.settings.save_no_test_item_id'))
        return
      }
      setTestItemId(createdId)
      setTestCreated(true)
      setManagerTestItemStatus(TEST_MANAGER_ITEM_STATUS_DRAFT)
      toasts.info(tm('modal.test_create.settings.save_success'))
    } catch (e) {
      const message =
        e instanceof Error ? e.message : t('modal.test_create.settings.save_error')
      toasts.error(message)
    } finally {
      setSettingsSaving(false)
    }
  }, [
    longreadId,
    token,
    toasts,
    t,
    title,
    description,
    headerBackgroundColor,
    passScore,
    durationMin,
    attemptsLimit,
    access,
    shuffleQuestions,
    shuffleAnswers,
    showResultsAfterAnswer,
    explainMode,
    testItemId,
    hasValidTitle,
    hasValidDurationMin,
    testPublishedLocked,
    isQuestionnaire,
    tm
  ])

  return (
    <Root>
      <Header direction='row' align='center'>
        <MainTitleWrap>
          <MainTitle>{modalTitle}</MainTitle>
        </MainTitleWrap>
        {(activeTab === 'settings' ||
          (isQuestionnaire && activeTab === 'params')) && (
          <HeaderSaveSlot>
            <BrgButton
              variant={ButtonVariant.Filled}
              fullWidth
              disabled={
                settingsSaving ||
                testPublishedLocked ||
                !longreadId ||
                !token?.trim() ||
                !hasValidTitle ||
                !hasValidDurationMin
              }
              onClick={handleSaveSettings}
            >
              {isEditMode
                ? t('modal.test_create.finish_button')
                : t('modal.test_create.settings.save_button')}
            </BrgButton>
          </HeaderSaveSlot>
        )}
        {activeTab !== 'settings' &&
          !(isQuestionnaire && activeTab === 'params') &&
          canFinishTest && (
          <HeaderSaveSlot>
            <BrgButton
              variant={ButtonVariant.Filled}
              fullWidth
              disabled={
                settingsSaving ||
                testPublishedLocked ||
                !longreadId ||
                !token?.trim() ||
                !hasValidTitle ||
                !hasValidDurationMin
              }
              onClick={() => void handleFinish()}
            >
              {t('modal.test_create.finish_button')}
            </BrgButton>
          </HeaderSaveSlot>
        )}
      </Header>

      <Body direction='row'>
        <Sidebar>
          {tabs.map((tab) => {
            const locked = isTabLocked(tab.id)
            const isActive = activeTab === tab.id && !locked
            return (
              <TabButton
                key={tab.id}
                type='button'
                $active={isActive}
                $disabled={locked}
                disabled={locked}
                onClick={() => {
                  if (locked) return
                  setActiveTab(tab.id)
                }}
              >
                {tab.label}
              </TabButton>
            )
          })}
        </Sidebar>

        <MainPanel>
          {testPublishedLocked && (
            <PublishedReadOnlyBanner role='status'>
              {tm('modal.test_create.published_readonly_banner')}
            </PublishedReadOnlyBanner>
          )}

          {activeTab === 'params' && (
            <TabPanelLocked $locked={testPublishedLocked}>
              <SectionTitle>{tm('modal.test_create.section_params')}</SectionTitle>

              <FieldBlock>
                <BilingualLangField
                  label={tm('modal.test_create.field_title')}
                  required
                  activeLang={titleLang}
                  onLangChange={setTitleLang}
                  value={title[titleLang]}
                  onChange={(v) => setTitleForLang(titleLang, v)}
                  placeholder={tm('modal.test_create.placeholder_title')}
                  maxLength={500}
                  inputMode='text'
                />
              </FieldBlock>

              <FieldBlock>
                <BilingualLangField
                  label={tm('modal.test_create.field_description')}
                  activeLang={descLang}
                  onLangChange={setDescLang}
                  value={description[descLang]}
                  onChange={(v) => setDescForLang(descLang, v)}
                  placeholder={tm('modal.test_create.placeholder_description')}
                  maxLength={MAX_DESCRIPTION}
                  inputMode='textarea'
                  charCount={{ current: descLength, max: MAX_DESCRIPTION }}
                />
              </FieldBlock>

              <FieldBlock>
                <ColorPicker
                  value={headerBackgroundColor}
                  onChange={setHeaderBackgroundColor}
                  formLabel={t('modal.test_create.header_background')}
                  showOnMobile
                  customPickerFirst
                  swatchColors={TEST_HEADER_SWATCH_PALETTE}
                />
              </FieldBlock>
            </TabPanelLocked>
          )}

          {activeTab === 'settings' && !isQuestionnaire && (
            <TabPanelLocked $locked={testPublishedLocked}>
              <SectionTitle>
                {t('modal.test_create.settings.section_title')}
              </SectionTitle>

              <>
              <FieldBlock>
                <SettingsFieldLabel>
                  {t('modal.test_create.settings.pass_score')}
                </SettingsFieldLabel>
                <SettingsInput
                  type='text'
                  inputMode='numeric'
                  autoComplete='off'
                  value={passScore}
                  onChange={(e) => {
                    const v = e.target.value.trim()
                    if (v === '') {
                      setPassScore('')
                      return
                    }
                    const digits = v.replace(/[^\d]/g, '')
                    if (digits === '') {
                      setPassScore('')
                      return
                    }
                    const n = parseInt(digits, 10)
                    if (!Number.isFinite(n)) {
                      setPassScore('')
                      return
                    }
                    setPassScore(String(Math.min(100, Math.max(0, n))))
                  }}
                />
              </FieldBlock>

              <FieldBlock>
                <SettingsFieldLabel $required>
                  {t('modal.test_create.settings.duration_min')}
                </SettingsFieldLabel>
                <SettingsInput
                  type='text'
                  inputMode='numeric'
                  autoComplete='off'
                  value={durationMin}
                  onChange={(e) => {
                    const v = e.target.value.trim()
                    if (v === '') {
                      setDurationMin('')
                      return
                    }
                    const digits = v.replace(/[^\d]/g, '')
                    if (digits === '') {
                      setDurationMin('')
                      return
                    }
                    const n = parseInt(digits, 10)
                    if (!Number.isFinite(n)) {
                      setDurationMin('')
                      return
                    }
                    if (n < 1) {
                      setDurationMin('')
                      return
                    }
                    setDurationMin(String(n))
                  }}
                />
              </FieldBlock>

              <FieldBlock>
                <SettingsFieldLabel>
                  {t('modal.test_create.settings.attempts_limit')}
                </SettingsFieldLabel>
                <SettingsInput
                  type='text'
                  inputMode='numeric'
                  autoComplete='off'
                  value={attemptsLimit}
                  onChange={(e) => {
                    const v = e.target.value.trim()
                    if (v === '') {
                      setAttemptsLimit('1')
                      return
                    }
                    const digits = v.replace(/[^\d]/g, '')
                    if (digits === '') {
                      setAttemptsLimit('1')
                      return
                    }
                    const n = parseInt(digits, 10)
                    if (!Number.isFinite(n)) {
                      setAttemptsLimit('1')
                      return
                    }
                    setAttemptsLimit(String(Math.max(1, n)))
                  }}
                />
              </FieldBlock>

              {/* Поле «Доступ» временно скрыто
              <FieldBlock>
                <CommonInput
                  label={t('modal.test_create.settings.access_label')}
                  orientation="column"
                  type={INPUT_TYPES.dropdown}
                  options={accessOptions as any}
                  value={accessValue as any}
                  onWhiteBG
                  onChange={(item: any) => {
                    const v = item?.value
                    if (v === 'open' || v === 'private') {
                      setAccess(v)
                    }
                  }}
                />
              </FieldBlock>
              */}

              <FieldBlock>
                <FlexBox direction='column' gy={14} style={{ width: '100%' }}>
                  <MagicCheckBox
                    checked={shuffleAnswers}
                    label={t('modal.test_create.settings.shuffle_answers')}
                    onWhiteBG
                    onClick={() => setShuffleAnswers((prev) => !prev)}
                  />
                  <MagicCheckBox
                    checked={shuffleQuestions}
                    label={t('modal.test_create.settings.shuffle_questions')}
                    onWhiteBG
                    onClick={() => setShuffleQuestions((prev) => !prev)}
                  />
                  <MagicCheckBox
                    checked={showResultsAfterAnswer}
                    label={t(
                      'modal.test_create.settings.show_results_after_answer'
                    )}
                    onWhiteBG
                    onClick={() =>
                      setShowResultsAfterAnswer((prev) => !prev)
                    }
                  />
                </FlexBox>
              </FieldBlock>

              <SettingsExplainFieldBlock $muted={!showResultsAfterAnswer}>
                <SettingsSubTitle>
                  {t('modal.test_create.settings.explain_title')}
                </SettingsSubTitle>
                <SettingsRadioStack>
                  <RadioButton
                    name={EXPLAIN_GROUP_NAME}
                    checked={explainMode === 'never'}
                    disabled={!showResultsAfterAnswer}
                    onChange={() => setExplainMode('never')}
                  >
                    <span className='radio-text'>
                      {t('modal.test_create.settings.explain_never')}
                    </span>
                  </RadioButton>
                  <RadioButton
                    name={EXPLAIN_GROUP_NAME}
                    checked={explainMode === 'after_submit'}
                    disabled={!showResultsAfterAnswer}
                    onChange={() => setExplainMode('after_submit')}
                  >
                    <span className='radio-text'>
                      {t('modal.test_create.settings.explain_after_submit')}
                    </span>
                  </RadioButton>
                  <RadioButton
                    name={EXPLAIN_GROUP_NAME}
                    checked={explainMode === 'if_passed'}
                    disabled={!showResultsAfterAnswer}
                    onChange={() => setExplainMode('if_passed')}
                  >
                    <span className='radio-text'>
                      {t('modal.test_create.settings.explain_if_passed')}
                    </span>
                  </RadioButton>
                  <RadioButton
                    name={EXPLAIN_GROUP_NAME}
                    checked={explainMode === 'after_each'}
                    disabled={!showResultsAfterAnswer}
                    onChange={() => setExplainMode('after_each')}
                  >
                    <span className='radio-text'>
                      {t('modal.test_create.settings.explain_after_each')}
                    </span>
                  </RadioButton>
                </SettingsRadioStack>
              </SettingsExplainFieldBlock>
              </>
            </TabPanelLocked>
          )}

          {showQuestionsLoading && (
            <SectionTitle>{t('modal.test_create.questions.loading')}</SectionTitle>
          )}

          {showQuestionsTabContent && (
            <TabPanelLocked
              $locked={testPublishedLocked}
              style={{
                display: activeTab === 'questions' ? 'block' : 'none'
              }}
            >
              <TestCreateQuestionsTab
                key={`${testItemId ?? 'new'}-${questionsHydrationKey}`}
                productKind={resolvedProductKind}
                testItemId={testItemId}
                accessToken={token}
                longreadId={longreadId}
                initialQuestions={reportQuestions}
                onQuestionsChange={handleQuestionsChange}
              />
            </TabPanelLocked>
          )}

          {/* {SHOW_TEST_CREATE_REPORT_TAB && activeTab === 'report' && (
            <TestCreateReportTab
              testTitle={title[titleLang]}
              testDescription={description[titleLang]}
              questions={reportQuestions}
              titleLang={titleLang}
              passScore={passScore}
              durationMin={durationMin}
              attemptsLimit={attemptsLimit}
              authorName={reportTabAuthorName}
              createdAtIso={testReportMeta.createdAtIso}
              testStatus={testReportMeta.status}
            />
          )} */}
        </MainPanel>
      </Body>
    </Root>
  )
}

export default TestCreateModal
